/**
 * RecoveryModeBanner - Deadline-Aware Sprint Banner
 * Displays deadline urgency with actionable daily sprint plan
 * Implements UX Philosophy: Confidence-Instilling, Contextually Intelligent
 */

import React, { useState, useEffect } from 'react';
import { AlertTriangle, Calendar, Lock, TrendingUp, Target, Zap, CheckCircle2, Clock, ChevronDown, ChevronUp, Calculator } from 'lucide-react';
import { getRecoveryStats } from '../../utils/taskUtils';
import DeadlineIntelligenceService from '../../services/DeadlineIntelligenceService';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import WorkloadCalculatorModal from './WorkloadCalculatorModal';
import { useSessionContext } from '../../contexts/SessionContext';

const RecoveryModeBanner = ({
  tasks = [],
  colleges = [],
  onFocusClick,
  onViewTimelineClick,
  className = ''
}) => {
  const stats = getRecoveryStats(tasks);

  // MVV workload state
  const [showWorkloadBreakdown, setShowWorkloadBreakdown] = useState(false);
  const [workloadData, setWorkloadData] = useState(null);
  const [loadingWorkload, setLoadingWorkload] = useState(false);
  const [savingMVV, setSavingMVV] = useState(false);

  // Workload Calculator Modal
  const [showWorkloadCalculator, setShowWorkloadCalculator] = useState(false);
  const { startSession } = useSessionContext();

  const handleStartSprint = (sprintData) => {
    // Start the session with the selected tasks
    startSession(sprintData);
    toast.success(`🚀 Sprint started with ${sprintData.tasks.length} tasks!`);
  };

  // Debug logging
  console.log('[RecoveryBanner] Received colleges:', colleges);
  console.log('[RecoveryBanner] Received tasks count:', tasks.length);

  // Get deadline context
  const deadlineInfo = DeadlineIntelligenceService.getCurrentPhase(colleges);
  console.log('[RecoveryBanner] Deadline info:', deadlineInfo);
  const phase = deadlineInfo?.phase || 'NORMAL';
  console.log('[RecoveryBanner] Phase:', phase);

  // Don't show in normal mode with no overdue tasks
  if (phase === 'NORMAL' && stats.overdueCount === 0) {
    return null;
  }

  // Determine mode and styling
  const isEmergency = phase === 'EMERGENCY_7';
  const isSprint = phase === 'SPRINT_14';
  const isCrunch = phase === 'CRUNCH_30';
  const isDeadlineMode = isEmergency || isSprint || isCrunch;

  // Recommend which tasks are good MVV candidates
  const getMVVRecommendations = (task, college) => {
    if (!task.mvv_time_required || task.mvv_time_required === task.estimated_duration) {
      return { recommended: false, reason: 'No MVV version available' };
    }

    const timeSaved = (task.estimated_duration - task.mvv_time_required) / 60; // in hours
    const collegeTier = college?.category || 'target';

    // High-value MVV: saves significant time with lower risk
    if (timeSaved >= 2) {
      // Research tasks are usually safe to do MVV
      if (task.category === 'research' || task.title?.toLowerCase().includes('research')) {
        return {
          recommended: true,
          priority: 'high',
          reason: `Saves ${timeSaved.toFixed(1)}h - Research task (lower risk)`,
          risk: 'low'
        };
      }

      // Safety schools can use MVV more liberally
      if (collegeTier === 'safety') {
        return {
          recommended: true,
          priority: 'high',
          reason: `Saves ${timeSaved.toFixed(1)}h - Safety school`,
          risk: 'low'
        };
      }

      // For reach/target schools, only recommend MVV for non-essay tasks
      if (task.category !== 'essay' && !task.title?.toLowerCase().includes('essay')) {
        return {
          recommended: true,
          priority: 'medium',
          reason: `Saves ${timeSaved.toFixed(1)}h - Non-essay task`,
          risk: 'medium'
        };
      }
    }

    // Moderate time savings
    if (timeSaved >= 1) {
      if (collegeTier === 'safety') {
        return {
          recommended: true,
          priority: 'medium',
          reason: `Saves ${timeSaved.toFixed(1)}h - Safety school`,
          risk: 'low'
        };
      }
    }

    return {
      recommended: false,
      reason: `Only saves ${timeSaved.toFixed(1)}h - Consider full version for ${collegeTier} school`,
      risk: 'high'
    };
  };

  // Calculate workload with MVV options
  const calculateWorkloadBreakdown = async () => {
    setLoadingWorkload(true);
    try {
      // Group tasks by college
      const collegeTasksMap = {};
      tasks.forEach(task => {
        if (task.college_id) {
          if (!collegeTasksMap[task.college_id]) {
            collegeTasksMap[task.college_id] = [];
          }
          collegeTasksMap[task.college_id].push(task);
        }
      });

      // Calculate hours for each college (standard and MVV)
      const collegeBreakdowns = colleges.map(college => {
        const collegeTasks = collegeTasksMap[college.id] || [];
        const incompleteTasks = collegeTasks.filter(t => t.completion_status !== 'completed');

        // Add MVV recommendations to each task
        const tasksWithRecommendations = incompleteTasks.map(task => ({
          ...task,
          mvvRecommendation: getMVVRecommendations(task, college)
        }));

        const standardHours = incompleteTasks.reduce((sum, task) => {
          return sum + ((task.estimated_duration || 0) / 60);
        }, 0);

        const mvvHours = incompleteTasks.reduce((sum, task) => {
          return sum + ((task.mvv_time_required || task.estimated_duration || 0) / 60);
        }, 0);

        const currentHours = incompleteTasks.reduce((sum, task) => {
          const time = task.mvv_selected
            ? (task.mvv_time_required || 0)
            : (task.estimated_duration || 0);
          return sum + (time / 60);
        }, 0);

        // Get recommended MVV tasks
        const recommendedMVVTasks = tasksWithRecommendations.filter(t =>
          t.mvvRecommendation.recommended && t.mvvRecommendation.priority === 'high'
        );

        return {
          college,
          tasks: tasksWithRecommendations,
          recommendedMVVTasks,
          standardHours: Math.round(standardHours * 10) / 10,
          mvvHours: Math.round(mvvHours * 10) / 10,
          currentHours: Math.round(currentHours * 10) / 10,
          timeSaved: Math.round((standardHours - mvvHours) * 10) / 10
        };
      });

      // Calculate totals with proper rounding
      const totals = {
        standardHours: Math.round(collegeBreakdowns.reduce((sum, c) => sum + c.standardHours, 0) * 10) / 10,
        mvvHours: Math.round(collegeBreakdowns.reduce((sum, c) => sum + c.mvvHours, 0) * 10) / 10,
        currentHours: Math.round(collegeBreakdowns.reduce((sum, c) => sum + c.currentHours, 0) * 10) / 10
      };

      // Calculate daily hours needed
      const daysUntil = deadlineInfo?.daysUntil || 30;
      totals.dailyStandard = Math.round((totals.standardHours / daysUntil) * 10) / 10;
      totals.dailyMVV = Math.round((totals.mvvHours / daysUntil) * 10) / 10;
      totals.dailyCurrent = Math.round((totals.currentHours / daysUntil) * 10) / 10;

      setWorkloadData({ colleges: collegeBreakdowns, totals, daysUntil });
    } catch (error) {
      console.error('Error calculating workload:', error);
      toast.error('Failed to calculate workload');
    } finally {
      setLoadingWorkload(false);
    }
  };

  // Toggle MVV for a specific college
  const toggleCollegeMVV = async (collegeId, enableMVV) => {
    setSavingMVV(true);
    try {
      // Get user ID from tasks
      const userId = tasks[0]?.user_id;
      if (!userId) throw new Error('No user ID found');

      const { error } = await supabase
        .from('timeline_items')
        .update({ mvv_selected: enableMVV })
        .eq('user_id', userId)
        .eq('college_id', collegeId)
        .neq('completion_status', 'completed');

      if (error) throw error;

      toast.success(enableMVV ? 'MVV mode enabled' : 'Standard mode enabled');

      // Recalculate workload
      await calculateWorkloadBreakdown();
    } catch (error) {
      console.error('Error toggling MVV:', error);
      toast.error('Failed to update mode');
    } finally {
      setSavingMVV(false);
    }
  };

  // Load workload data when breakdown is shown
  useEffect(() => {
    if (showWorkloadBreakdown && !workloadData) {
      calculateWorkloadBreakdown();
    }
  }, [showWorkloadBreakdown]);

  // Get workload scenario if in deadline mode
  const scenarios = isDeadlineMode && colleges.length > 0
    ? DeadlineIntelligenceService.generateScenarios(colleges, tasks)
    : [];
  const recommendedScenario = scenarios.find(s => s.feasible) || scenarios[0];

  // Get daily breakdown
  const dailyBreakdown = recommendedScenario && deadlineInfo?.daysUntil
    ? DeadlineIntelligenceService.generateDailyBreakdown(recommendedScenario, deadlineInfo.daysUntil)
    : [];

  // Choose banner color based on phase
  const bannerColor = isEmergency
    ? 'from-red-600 to-red-700'
    : isSprint
      ? 'from-orange-500 to-orange-600'
      : isCrunch
        ? 'from-yellow-500 to-orange-500'
        : 'from-orange-600 to-red-600';

  return (
    <div className={`bg-gradient-to-r ${bannerColor} text-white rounded-lg shadow-lg p-6 ${className}`}>
      <div className="flex items-start justify-between gap-4">
        {/* Main Content */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              {isDeadlineMode ? (
                <Target className="w-6 h-6 text-white" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-xl font-bold">
                {isEmergency && `🎯 ${deadlineInfo.college} ${deadlineInfo.round?.replace('_', ' ').toUpperCase()} - ${deadlineInfo.daysUntil} DAYS`}
                {isSprint && `⚡ ${deadlineInfo.college} Sprint - ${deadlineInfo.daysUntil} Days`}
                {isCrunch && `📅 Application Sprint - ${deadlineInfo.daysUntil} Days Until ${deadlineInfo.college}`}
                {!isDeadlineMode && 'Recovery Mode Active'}
              </h3>
              <p className="text-white/90 text-sm">
                {isDeadlineMode
                  ? 'Your focused plan to nail this deadline'
                  : 'Let\'s get you back on track - you\'ve got this!'
                }
              </p>
            </div>
          </div>

          {/* Content based on mode */}
          {isDeadlineMode ? (
            <>
              {/* Daily Breakdown */}
              {dailyBreakdown.length > 0 && (
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 mb-4">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Daily Sprint Plan
                  </h4>
                  <div className="space-y-2">
                    {dailyBreakdown.slice(0, 5).map((day, idx) => (
                      <div
                        key={idx}
                        className={`text-sm flex items-center justify-between ${
                          day.isDeadline ? 'font-bold text-white' : 'text-white/90'
                        }`}
                      >
                        <span>
                          {day.days}: {day.task}
                        </span>
                        {day.hours > 0 && (
                          <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                            {day.hours}h
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Workload Scenarios */}
              {scenarios.length > 0 && (
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-sm">Workload Options</h4>
                    <button
                      onClick={() => setShowWorkloadBreakdown(!showWorkloadBreakdown)}
                      className="text-xs bg-white/20 hover:bg-white/30 px-3 py-1 rounded-md transition-colors flex items-center gap-1"
                    >
                      {showWorkloadBreakdown ? (
                        <>
                          <ChevronUp className="w-3 h-3" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-3 h-3" />
                          See Hours Breakdown
                        </>
                      )}
                    </button>
                  </div>

                  <div className="space-y-2">
                    {scenarios.slice(0, 3).map((scenario, idx) => (
                      <div
                        key={idx}
                        className={`text-sm p-2 rounded ${
                          scenario.feasible
                            ? 'bg-white/10'
                            : 'bg-red-900/30 border border-red-400/30'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{scenario.name}</span>
                          <span className="text-xs">
                            {scenario.dailyHours} hrs/day
                          </span>
                        </div>
                        <p className="text-xs text-white/80">
                          {scenario.recommendation}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* MVV Workload Breakdown */}
                  {showWorkloadBreakdown && (
                    <div className="mt-4 pt-4 border-t border-white/20">
                      {loadingWorkload ? (
                        <div className="flex items-center justify-center py-4">
                          <Clock className="w-4 h-4 animate-spin mr-2" />
                          <span className="text-sm">Calculating workload...</span>
                        </div>
                      ) : workloadData ? (
                        <div className="space-y-4">
                          {/* Total Hours Summary */}
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                              <div className="text-xs opacity-80 mb-1">Standard</div>
                              <div className="text-lg font-bold">{workloadData.totals.standardHours}h</div>
                              <div className="text-xs opacity-70">{workloadData.totals.dailyStandard}h/day</div>
                            </div>
                            <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                              <div className="text-xs opacity-80 mb-1">MVV Mode</div>
                              <div className="text-lg font-bold text-orange-200">{workloadData.totals.mvvHours}h</div>
                              <div className="text-xs opacity-70">{workloadData.totals.dailyMVV}h/day</div>
                            </div>
                            <div className="bg-white/10 rounded-lg p-3 border border-white/20">
                              <div className="text-xs opacity-80 mb-1">Current</div>
                              <div className="text-lg font-bold text-green-200">{workloadData.totals.currentHours}h</div>
                              <div className="text-xs opacity-70">{workloadData.totals.dailyCurrent}h/day</div>
                            </div>
                          </div>

                          {/* Recommended MVV Tasks (High Priority) */}
                          {workloadData.colleges.some(c => c.recommendedMVVTasks?.length > 0) && (
                            <div className="bg-green-500/10 border border-green-400/30 rounded-lg p-3">
                              <h5 className="text-xs font-semibold uppercase mb-2 flex items-center gap-2">
                                <Zap className="w-4 h-4 text-green-300" />
                                Recommended Time Savers
                              </h5>
                              <p className="text-xs opacity-80 mb-3">
                                These tasks are good candidates for MVV mode (lower risk, significant time savings):
                              </p>
                              <div className="space-y-1">
                                {workloadData.colleges.map((collegeData) =>
                                  collegeData.recommendedMVVTasks?.map((task, idx) => (
                                    <div key={`${collegeData.college.id}-${idx}`} className="text-xs bg-white/5 rounded p-2">
                                      <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1">
                                          <span className="font-medium">{task.title}</span>
                                          <span className="opacity-70 text-[10px] ml-2">
                                            ({collegeData.college.name || collegeData.college.global_colleges?.name})
                                          </span>
                                        </div>
                                        <span className="text-green-300 font-semibold whitespace-nowrap">
                                          {task.mvvRecommendation.reason}
                                        </span>
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}

                          {/* Per-College Breakdown */}
                          <div className="space-y-2">
                            <h5 className="text-xs font-semibold uppercase opacity-80">Per-College Breakdown</h5>
                            {workloadData.colleges.map((collegeData, idx) => {
                              const isMVV = collegeData.tasks.some(t => t.mvv_selected);
                              const collegeName = collegeData.college.name || collegeData.college.global_colleges?.name;

                              return (
                                <div key={idx} className="bg-white/5 rounded-lg p-3 border border-white/10">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{collegeName}</span>
                                      {collegeData.recommendedMVVTasks?.length > 0 && (
                                        <span className="text-[10px] bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded">
                                          {collegeData.recommendedMVVTasks.length} recommended
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => toggleCollegeMVV(collegeData.college.id, !isMVV)}
                                        disabled={savingMVV}
                                        className={`text-xs px-2 py-1 rounded transition-colors ${
                                          isMVV
                                            ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                            : 'bg-white/20 hover:bg-white/30'
                                        } disabled:opacity-50`}
                                      >
                                        {isMVV ? '✓ MVV' : 'Standard'}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div>
                                      <span className="opacity-70">Std: </span>
                                      <span className="font-semibold">{collegeData.standardHours}h</span>
                                    </div>
                                    <div>
                                      <span className="opacity-70">MVV: </span>
                                      <span className="font-semibold text-orange-200">{collegeData.mvvHours}h</span>
                                    </div>
                                    <div>
                                      <span className="opacity-70">Current: </span>
                                      <span className="font-semibold text-green-200">{collegeData.currentHours}h</span>
                                    </div>
                                  </div>
                                  {collegeData.timeSaved > 0 && (
                                    <p className="text-xs opacity-70 mt-1">
                                      Save {collegeData.timeSaved}h with MVV mode
                                    </p>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* MVV Warning */}
                          <div className="bg-amber-500/20 border border-amber-400/30 rounded-lg p-3">
                            <p className="text-xs leading-relaxed">
                              <strong>⚠️ MVV Mode:</strong> Reduces time but may affect application quality.
                              Use strategically for safety schools or when time is extremely limited.
                            </p>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )}

              {/* Encouraging Message for Deadline Mode */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-sm leading-relaxed">
                  <span className="font-semibold">You've got this! </span>
                  {recommendedScenario?.dailyHours <= 5
                    ? `Your ${recommendedScenario.dailyHours} hrs/day pace is realistic with built-in buffer time. Stay focused and you'll nail this deadline.`
                    : `This timeline is tight at ${recommendedScenario?.dailyHours} hrs/day. Consider focusing only on ${deadlineInfo.college} to ensure quality work.`
                  }
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Recovery Mode Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-orange-200" />
                    <span className="text-xs font-medium text-orange-100 uppercase tracking-wide">
                      Overdue
                    </span>
                  </div>
                  <p className="text-2xl font-bold">{stats.overdueCount}</p>
                  <p className="text-xs text-orange-200 mt-0.5">
                    task{stats.overdueCount !== 1 ? 's' : ''} need{stats.overdueCount === 1 ? 's' : ''} attention
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-orange-200" />
                    <span className="text-xs font-medium text-orange-100 uppercase tracking-wide">
                      This Week
                    </span>
                  </div>
                  <p className="text-2xl font-bold">{stats.thisWeekCount}</p>
                  <p className="text-xs text-orange-200 mt-0.5">
                    upcoming deadline{stats.thisWeekCount !== 1 ? 's' : ''}
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-4 h-4 text-orange-200" />
                    <span className="text-xs font-medium text-orange-100 uppercase tracking-wide">
                      Blocked
                    </span>
                  </div>
                  <p className="text-2xl font-bold">{stats.blockedCount}</p>
                  <p className="text-xs text-orange-200 mt-0.5">
                    waiting on prerequisites
                  </p>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                <p className="text-sm leading-relaxed">
                  <span className="font-semibold">You've got this! </span>
                  We've organized your tasks into a smart recovery plan.
                  Focus on the priorities below and you'll be back on track quickly.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 flex flex-col gap-2">
          <button
            onClick={() => setShowWorkloadCalculator(true)}
            className="bg-white text-[#26a69a] px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors shadow-lg hover:shadow-xl flex items-center gap-2 group border-2 border-slate-900"
          >
            <Calculator className="w-5 h-5" />
            <span>Plan Workload</span>
          </button>
          {onViewTimelineClick && isDeadlineMode && (
            <button
              onClick={onViewTimelineClick}
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg font-medium transition-colors text-sm"
            >
              View Full Timeline
            </button>
          )}
        </div>
      </div>

      {/* Workload Calculator Modal */}
      <WorkloadCalculatorModal
        isOpen={showWorkloadCalculator}
        onClose={() => setShowWorkloadCalculator(false)}
        colleges={colleges}
        tasks={tasks}
        deadlineInfo={deadlineInfo}
        onStartSprint={handleStartSprint}
      />
    </div>
  );
};

export default RecoveryModeBanner;
